<template>
	<div
		class="hdl-dropdown-question question"
		role="dialog"
		:aria-labelledby="`${question.uuid}-label`"
		:id="`${question.uuid}-container`"
		:class="{
			'has-error': errorMap.size > 0
		}"
		@keydown="keydownHandler"
		v-show="question.visible"
	>
		<div
			ref="rendered"
			v-html="question.renderedQuestion"
		/>
		<div class="errors" :id="`errs-${question.uuid}`" v-if="errorMessages.length" aria-live="assertive">
			<ul>
				<li v-for="errorMessage of errorMessages" :key="errorMessage">
					{{ errorMessage }}
				</li>
			</ul>
		</div>
		<dropdown-question-component
			:model-value="response"
			@update:model-value="responseChanged"
			:is-multi-select="isMultiSelect"
			:choices="dropdownChoices"
			:placeholder="placeholder"
			:minimum="question.limitMin"
			:maximum="question.limitMax"
			ref="dropdownComponent"
		/>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedQuestionChoiceType, PublishedSurveyQuestionType, QuestionChoiceBadge, QuestionOptionKey, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ChoiceResponse, ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';
import { inPlaceShuffleArray, indexToAlpha } from '@/common/utils';
import { DropdownChoice, DropdownChoices } from '@/components/common/types';
import DropdownQuestionComponent from '@/components/surveys/DropdownQuestionComponent.vue';

@Component({
	emits: ['intentionalBlur']
})
export default class SingleChoice extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: Array<string> | null;

	errorMap: Map<SurveyResponseErrorCode, string> = new Map();

	mounted(): void {
		this.$el.__vue__ = this;

		this.errorMap = new Map();

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			if (existingResponse && existingResponse.choices && existingResponse.choices.length) {
				this.response = existingResponse.choices.map((cr: ChoiceResponse) => cr.choiceUuid);
			} else {
				this.response = null;
			}

		}

		this.accessorizeRendered();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const dropdown = this.$refs.dropdownComponent as DropdownQuestionComponent | undefined;

		if (dropdown === undefined) {
			return true;
		}

		dropdown.focusTrigger();

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		} else if (event.key === 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'up');
		}
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);

			if (this.question.required) {
				const sroRequiredIndicator = document.createElement('span');
				sroRequiredIndicator.classList.add('hdl-sr-only');
				sroRequiredIndicator.textContent = '(required)';
				labelTail.appendChild(sroRequiredIndicator);
				const requiredIndicator = document.createElement('div');
				requiredIndicator.setAttribute('aria-hidden', 'true');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	responseChanged(selectedChoices: Array<string> | null): void {
		this.response = selectedChoices;
		this.clearError(SurveyResponseErrorCode.MISSING_REQUIRED);

		if (this.question.limitMin && this.question.limitMin > 0) {
			if (selectedChoices && selectedChoices.length >= this.question.limitMin) {
				this.clearError(SurveyResponseErrorCode.UNDER_MINIMUM);
			}
		}

		if (this.question.limitMax && this.question.limitMax > 0) {
			if (selectedChoices && selectedChoices.length <= this.question.limitMax) {
				this.clearError(SurveyResponseErrorCode.OVER_LIMIT);
			}
		}

		if (this.responseGroup) {
			this.responseGroup.setResponse(
				this.question.uuid,
				null,
				null,
				(selectedChoices)?
						selectedChoices.map((choiceUuid: string) => ({ choiceUuid }))
					: null,
				null
			);
		}

	}

	getResponse(): Array<string> | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null;
	}

	clearError(errorCode: SurveyResponseErrorCode): void {
		this.errorMap.delete(errorCode);
	}

	markError(error: SurveyResponseInputErrorType): void {
		this.errorMap.set(error.code, error.error);
	}

	get errorMessages(): Array<string> {
		return Array.from(this.errorMap.values());
	}

	get dropdownChoices(): DropdownChoices<string> {
		if (this.question === null || this.question.bulkChoices === null) {
			return [];
		}

		const choices = this.question.bulkChoices.choices.map(
			(choice: { label: string, uuid: string }) => ({
				label: choice.label,
				value: choice.uuid
			})
		);

		if (this.question.randomizeChoices) {
			inPlaceShuffleArray<DropdownChoice<string>>(choices);
		}

		return choices;
	}

	get placeholder(): string {
		if (this.question === null) {
			return '';
		}

		const placeholder = this.question.options[QuestionOptionKey.PLACEHOLDER];

		if (placeholder === undefined) {
			return '';
		}

		return placeholder.charValue!;
	}

	get isMultiSelect(): boolean {
		if (this.question === null) {
			return false;
		}

		const isMultiSelect = this.question.options[QuestionOptionKey.MULTI_SELECT];

		if (isMultiSelect === undefined) {
			return false;
		}

		return !!isMultiSelect.intValue!;
	}

	get choices(): Array<PublishedQuestionChoiceType> {
		if (this.question === null) {
			return [];
		}

		if (!this.question.randomizeChoices) {
			return this.question.choices;
		}

		const shuffled = [...this.question.choices];
		inPlaceShuffleArray<PublishedQuestionChoiceType>(shuffled);
		return shuffled;
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-dropdown-question {
	@apply relative;

	&.has-error {
		label {
			@apply text-hdl-wc-red;
		}
	}

	.errors {
		@apply text-hdl-wc-red text-sm;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				margin: 0;
				list-style-type: none;
			}
		}
	}
}
</style>
